import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogSelectTrigger,
  DialogTitle,
} from "@/lib/components/Dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/lib/components/Select";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { useCallback, useEffect, useMemo, useState } from "react";
import WaveformPlayer from "./WaveformPlayer";
import {
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
  IconSearch,
  IconHistory,
  IconUserPlus,
  IconUsers,
} from "@tabler/icons-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import { Button } from "@/lib/components/Button/Button";
import { useIsFeatureEnabled } from "@/lib/feature-flags";
import AdminView from "./AdminView";
import { cn, getLanguageName, getVoiceActorAvatar } from "@/lib/utils";
import { VoiceActorDto } from "@repo/api/voice-actor";
import { usePreviouslyUsedVoiceActorsQuery } from "@/lib/queries/voice-over-queries";
import GradientTabButton from "@/lib/components/Button/GradientTabButton";

type VoiceActorDialogTab = "ADMIN_VIEW" | "ALL_VOICE_ACTORS" | "RECENTLY_USED";

function VoiceActorDialog({ error }: { error?: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const isAdmin = useIsFeatureEnabled();
  const [activeTab, setActiveTab] =
    useState<VoiceActorDialogTab>("ALL_VOICE_ACTORS");
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<FilterState>({
    language: "",
    age: "",
    gender: "",
    accent: "",
  });

  const [previewVoiceActor, setPreviewVoiceActor] =
    useState<VoiceActorDto | null>(null);
  const [isPreviewVoiceActorPlaying, setIsPreviewVoiceActorPlaying] =
    useState(false);

  const voiceActors = useEditorStore((state) => state.voiceActors);
  const voiceOverForm = useEditorStore((state) => state.voiceOverForm);
  const service = useEditorStore((state) => state.service);
  const videoIsPlaying = useEditorStore(
    (state) => state.videoPreviewState.isPlaying,
  );

  const selectedVoiceActor = voiceActors.find(
    (actor) => actor.externalId === voiceOverForm.voiceActorExternalId,
  );
  const teamExternalId = useEditorStore((state) => state.teamExternalId);

  const {
    data: { externalIds: previouslyUsedVoiceActorIds } = { externalIds: [] },
  } = usePreviouslyUsedVoiceActorsQuery(teamExternalId);

  const voiceActorsForTab = useMemo(() => {
    const voiceActorMap = Object.fromEntries(
      voiceActors.map((actor) => [actor.externalId, actor]),
    );

    if (activeTab === "RECENTLY_USED") {
      return previouslyUsedVoiceActorIds
        .map((id) => voiceActorMap[id])
        .filter((actor): actor is VoiceActorDto => actor !== undefined);
    }
    return voiceActors;
  }, [voiceActors, activeTab, previouslyUsedVoiceActorIds]);

  const filteredVoiceActors = useMemo(() => {
    return voiceActorsForTab.filter((actor) => {
      const matchesSearch = actor.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

      const matchesAge =
        !filters.age || AgeMap[actor.metadata?.age || ""] === filters.age;

      const primaryLanguage = actor.metadata?.languages[0];

      const matchesLanguage =
        !filters.language ||
        (!!primaryLanguage &&
          getLanguageName(primaryLanguage.language) === filters.language);

      const matchesGender =
        !filters.gender || actor.metadata?.gender === filters.gender;

      const matchesAccent =
        !filters.accent || primaryLanguage?.accent === filters.accent;

      return (
        matchesSearch &&
        matchesAge &&
        matchesLanguage &&
        matchesGender &&
        matchesAccent
      );
    });
  }, [voiceActorsForTab, searchQuery, filters]);

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handlePlayClick = useCallback(
    (actor: VoiceActorDto) => {
      const isCurrentPreview =
        previewVoiceActor?.externalId === actor.externalId;

      if (isCurrentPreview) {
        setIsPreviewVoiceActorPlaying((prev) => !prev);
      } else {
        setPreviewVoiceActor(actor);
        setIsPreviewVoiceActorPlaying(true);
      }
    },
    [previewVoiceActor?.externalId],
  );

  const handleUseVoiceActor = useCallback(
    (actor: VoiceActorDto) => {
      service.updateVoiceOverForm((form) => {
        form.voiceActorExternalId = actor.externalId;
      });
      setPreviewVoiceActor(null);
      setIsPreviewVoiceActorPlaying(false);
      setIsOpen(false);
    },
    [service],
  );

  useEffect(() => {
    if (!isOpen) {
      setPreviewVoiceActor(null);
      setIsPreviewVoiceActorPlaying(false);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <div className="min-w-0 flex-1">
        <div className="input-label">Voice Actor</div>
        <DialogSelectTrigger error={!!error}>
          {selectedVoiceActor
            ? selectedVoiceActor.name
            : "Select Voice Actor"}{" "}
        </DialogSelectTrigger>
        <p
          className={cn(
            "mt-1.5 text-sm text-destructive",
            !error && "invisible",
          )}
        >
          {error ?? "\u00A0"}
        </p>
      </div>
      <DialogContent
        className="mx-auto h-full max-h-175 w-[calc(100%-48px)] max-w-273 border-none"
        classNameInner="flex flex-col gap-0 p-0 sm:px-0"
        showCloseButton={false}
      >
        <DialogHeader
          variant="bordered"
          showCloseButton
          className="min-h-18 px-8 pr-5"
        >
          <DialogTitle>Voiceactors</DialogTitle>
        </DialogHeader>
        <div className="flex flex-1">
          <aside className="flex w-71.25 flex-col border-r border-grey-200">
            <Tabs
              isAdmin={isAdmin}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
            {activeTab !== "ADMIN_VIEW" && (
              <Filters filters={filters} onFilterChange={handleFilterChange} />
            )}
          </aside>
          <div className="flex flex-1 flex-col">
            {activeTab === "ADMIN_VIEW" ? (
              <AdminView />
            ) : (
              <>
                <SearchBar
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                />
                <Voiceactors
                  voiceActors={filteredVoiceActors}
                  previewVoiceActor={previewVoiceActor}
                  isPreviewVoiceActorPlaying={isPreviewVoiceActorPlaying}
                  filters={filters}
                  onPlayClick={handlePlayClick}
                  onUseVoiceActor={handleUseVoiceActor}
                />
                <PreviewVoiceActor
                  previewVoiceActor={previewVoiceActor}
                  isPreviewVoiceActorPlaying={isPreviewVoiceActorPlaying}
                  setIsPreviewVoiceActorPlaying={setIsPreviewVoiceActorPlaying}
                  videoIsPlaying={videoIsPlaying}
                />
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Tabs({
  isAdmin,
  activeTab,
  onTabChange,
}: {
  isAdmin: boolean;
  activeTab: VoiceActorDialogTab;
  onTabChange: (tab: VoiceActorDialogTab) => void;
}) {
  return (
    <div className="flex flex-col gap-3 border-b border-grey-200 p-6">
      {[
        ...(isAdmin
          ? [
              {
                id: "ADMIN_VIEW" as const,
                label: "Add Voice Actor",
                icon: IconUserPlus,
              },
            ]
          : []),
        {
          id: "ALL_VOICE_ACTORS" as const,
          label: "All Voice Actors",
          icon: IconUsers,
        },
        {
          id: "RECENTLY_USED" as const,
          label: "Recently Used",
          icon: IconHistory,
        },
      ].map((tab) => (
        <GradientTabButton
          key={tab.id}
          icon={tab.icon}
          label={tab.label}
          isActive={activeTab === tab.id}
          onClick={() => onTabChange(tab.id)}
        />
      ))}
    </div>
  );
}

const AgeMap: { [key: string]: string } = {
  young: "Young",
  middle_aged: "Middle Aged",
  senior: "Senior",
};

type FilterState = {
  language: string;
  age: string;
  gender: string;
  accent: string;
};

const SKIP_ACCENTS = ["standard", "modern standard"];

function Filters({
  filters,
  onFilterChange,
}: {
  filters: FilterState;
  onFilterChange: (key: keyof FilterState, value: string) => void;
}) {
  const voiceActors = useEditorStore((state) => state.voiceActors);

  const filterOptions = useMemo(() => {
    const languageAccentsMap = new Map<string, Set<string>>();
    const ages = new Set<string>();
    const genders = new Set<string>();

    voiceActors.forEach((actor) => {
      const primaryLanguage = actor.metadata?.languages[0];
      const languageName =
        primaryLanguage && getLanguageName(primaryLanguage.language);
      if (primaryLanguage && languageName) {
        if (!languageAccentsMap.has(languageName)) {
          languageAccentsMap.set(languageName, new Set<string>());
        }

        if (
          primaryLanguage.accent &&
          !SKIP_ACCENTS.includes(primaryLanguage.accent.toLowerCase())
        ) {
          languageAccentsMap.get(languageName)!.add(primaryLanguage.accent);
        }
      }

      if (actor.metadata?.age) {
        const age = AgeMap[actor.metadata.age];
        if (age) {
          ages.add(age);
        }
      }

      if (actor.metadata?.gender) {
        genders.add(actor.metadata.gender);
      }
    });

    const languages: Record<string, string[]> = {};
    Array.from(languageAccentsMap.keys())
      .sort()
      .forEach((lang) => {
        languages[lang] = Array.from(languageAccentsMap.get(lang)!).sort();
      });

    return {
      languages,
      ages: Array.from(ages).sort(),
      genders: Array.from(genders).sort(),
    };
  }, [voiceActors]);

  const languageNames = Object.keys(filterOptions.languages);
  const accents = filters.language
    ? filterOptions.languages[filters.language] || []
    : [];

  const handleLanguageChange = (value: string) => {
    onFilterChange("accent", "");
    onFilterChange("language", value === "ALL" ? "" : value);
  };

  const handleClearAll = () => {
    onFilterChange("language", "");
    onFilterChange("accent", "");
    onFilterChange("age", "");
    onFilterChange("gender", "");
  };

  const hasActiveFilters =
    filters.language || filters.accent || filters.age || filters.gender;

  return (
    <div className="flex-1 p-6">
      <h2 className="mb-5 font-semibold text-text-primary">Filters</h2>
      <div className="flex flex-col gap-4">
        <div className="relative">
          <Select
            value={filters.language || "ALL"}
            onValueChange={handleLanguageChange}
          >
            <SelectTrigger
              className={cn(
                "w-full capitalize",
                !filters.language && "text-text-secondary",
              )}
            >
              <SelectValue>
                {filters.language || "Primary Languages"}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Primary Languages</SelectItem>
              {languageNames.map((option) => (
                <SelectItem key={option} value={option} className="capitalize">
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="relative">
          <Tooltip>
            <TooltipTrigger asChild disabled={accents.length > 0}>
              <div>
                <Select
                  value={filters.accent || "ALL"}
                  onValueChange={(value) =>
                    onFilterChange("accent", value === "ALL" ? "" : value)
                  }
                  disabled={accents.length === 0}
                >
                  <SelectTrigger
                    className={cn(
                      "w-full capitalize",
                      !filters.accent && "text-text-secondary",
                    )}
                  >
                    <SelectValue>{filters.accent || "Accents"}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Accents</SelectItem>
                    {accents.map((option) => (
                      <SelectItem
                        key={option}
                        value={option}
                        className="capitalize"
                      >
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TooltipTrigger>
            {accents.length === 0 && (
              <TooltipContent>
                {filters.language
                  ? "This language doesn't have any accent variations available."
                  : "Select a language to see available accents."}
              </TooltipContent>
            )}
          </Tooltip>
        </div>

        <div className="relative">
          <Select
            value={filters.age || "ALL"}
            onValueChange={(value) =>
              onFilterChange("age", value === "ALL" ? "" : value)
            }
          >
            <SelectTrigger
              className={cn(
                "w-full capitalize",
                !filters.age && "text-text-secondary",
              )}
            >
              <SelectValue>{filters.age || "Ages"}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Ages</SelectItem>
              {filterOptions.ages.map((option) => (
                <SelectItem key={option} value={option} className="capitalize">
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="relative">
          <Select
            value={filters.gender || "ALL"}
            onValueChange={(value) =>
              onFilterChange("gender", value === "ALL" ? "" : value)
            }
          >
            <SelectTrigger
              className={cn(
                "w-full capitalize",
                !filters.gender && "text-text-secondary",
              )}
            >
              <SelectValue>{filters.gender || "Genders"}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Genders</SelectItem>
              {filterOptions.genders.map((option) => (
                <SelectItem key={option} value={option} className="capitalize">
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {hasActiveFilters && (
          <button
            onClick={handleClearAll}
            className="mt-2 text-sm font-medium text-text-secondary"
            disabled={!hasActiveFilters}
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}

const SearchBar = ({
  searchQuery,
  onSearchChange,
}: {
  searchQuery: string;
  onSearchChange: (query: string) => void;
}) => {
  return (
    <div className="mt-4 px-8">
      <div className="relative">
        <IconSearch
          className="absolute top-1/2 left-3.5 -translate-y-1/2 text-grey-400"
          size={16}
          stroke={2.5}
        />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by voice actor name..."
          className="border-grey-300 w-full rounded-md border border-grey-150 bg-grey-100 px-4 py-2 pl-10 text-sm font-medium text-grey-400 placeholder:text-text-secondary focus-visible:outline-tertiary-400"
        />
      </div>
    </div>
  );
};

const Voiceactors = ({
  voiceActors,
  previewVoiceActor,
  isPreviewVoiceActorPlaying,
  filters,
  onPlayClick,
  onUseVoiceActor,
}: {
  voiceActors: VoiceActorDto[];
  previewVoiceActor: VoiceActorDto | null;
  isPreviewVoiceActorPlaying: boolean;
  filters: FilterState;
  onPlayClick: (actor: VoiceActorDto) => void;
  onUseVoiceActor: (actor: VoiceActorDto) => void;
}) => {
  const [hoveredActorId, setHoveredActorId] = useState<string | null>(null);

  // Alphabetically sort voice actors, but prioritize voice actors that match the selected language
  const sortedAlphabeticallyVoiceActors = useMemo(
    () =>
      [...voiceActors].sort((a, b) => {
        if (!filters.language) {
          return a.name.localeCompare(b.name);
        }

        const aLanguageCode = a.metadata?.languages[0]?.language;
        const bLanguageCode = b.metadata?.languages[0]?.language;

        const aLanguageMatch =
          aLanguageCode && getLanguageName(aLanguageCode) === filters.language;
        const bLanguageMatch =
          bLanguageCode && getLanguageName(bLanguageCode) === filters.language;

        if (aLanguageMatch && !bLanguageMatch) {
          return -1;
        }

        if (!aLanguageMatch && bLanguageMatch) {
          return 1;
        }

        return a.name.localeCompare(b.name);
      }),
    [voiceActors, filters],
  );

  if (sortedAlphabeticallyVoiceActors.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-8 py-12">
        <IconSearch className="mb-3 text-text-primary" size={32} stroke={1.5} />
        <p className="mb-0.5 text-sm font-medium text-text-secondary">
          No voice actors found
        </p>
        <p className="text-sm text-text-secondary">
          Try adjusting your search or filters
        </p>
      </div>
    );
  }

  return (
    <div className="relative grow overflow-hidden rounded-br-xl">
      <div className="absolute inset-0 flex flex-col overflow-y-auto px-8 py-4 pb-8">
        {sortedAlphabeticallyVoiceActors.map((actor) => {
          const isCurrentPreview =
            previewVoiceActor?.externalId === actor.externalId;
          const isPlaying = isCurrentPreview && isPreviewVoiceActorPlaying;
          const isHovered = hoveredActorId === actor.externalId;
          const description = getFormattedDescription(actor);
          const voiceActorAvatar = getVoiceActorAvatar(actor.externalId);
          const primaryLanguage = actor.metadata?.languages[0]?.language;

          return (
            <div
              key={actor.externalId}
              className={cn(
                "-mx-3 flex h-14 flex-col gap-4 rounded-lg px-3 py-2 transition-colors duration-100 focus-within:bg-grey-100 hover:bg-grey-100",
              )}
              onFocus={() => setHoveredActorId(actor.externalId)}
              onMouseEnter={() => setHoveredActorId(actor.externalId)}
              onMouseLeave={() => setHoveredActorId(null)}
            >
              <div className="flex flex-1 flex-row items-center gap-3">
                <div className="flex flex-1 flex-row gap-3">
                  <div className="mt-0.5 min-h-6 min-w-6">
                    <img
                      src={voiceActorAvatar}
                      alt={actor.name}
                      width={24}
                      height={24}
                      className="rounded-full"
                    />
                  </div>
                  <div className="flex flex-col justify-center gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="line-clamp-1 text-sm font-semibold text-text-primary">
                        {actor.name}
                      </span>
                    </div>
                    {description ? (
                      <span className="line-clamp-1 text-[13px] text-text-secondary capitalize">
                        {description}
                      </span>
                    ) : null}
                  </div>
                </div>
                {primaryLanguage && (
                  <div className="flex-1">
                    <Tooltip delayDuration={100}>
                      <TooltipTrigger className="flex flex-col items-start gap-0.5">
                        <span className="text-[13px] font-medium">
                          {getLanguageName(primaryLanguage)}
                        </span>
                        <span className="text-[13px] text-grey-400">
                          +87 Languages
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-64">
                        {getLanguageName(primaryLanguage)} is this actor&apos;s
                        primary language, but they can also speak 85+ other
                        languages.
                      </TooltipContent>
                    </Tooltip>
                  </div>
                )}
                <div
                  className={cn(
                    "flex items-center gap-3",
                    isHovered ? "opacity-100" : "opacity-0",
                  )}
                >
                  <PlayPreviewButton
                    actor={actor}
                    isPlaying={isPlaying}
                    onPlayClick={onPlayClick}
                  />
                  <Button
                    variant="outline"
                    onClick={() => onUseVoiceActor(actor)}
                  >
                    Use
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="pointer-events-none absolute right-0 -bottom-1 left-0 h-16 bg-linear-to-t from-white" />
    </div>
  );
};

const PlayPreviewButton = ({
  actor,
  isPlaying,
  onPlayClick,
}: {
  actor: VoiceActorDto;
  isPlaying: boolean;
  onPlayClick: (actor: VoiceActorDto) => void;
}) => {
  const isButtonEnabled = !!actor.previewSrc;
  const button = (
    <Button
      variant="outline"
      size="icon"
      onClick={() => onPlayClick(actor)}
      disabled={!isButtonEnabled}
    >
      {isPlaying ? <IconPlayerPauseFilled /> : <IconPlayerPlayFilled />}
    </Button>
  );

  if (isButtonEnabled) {
    return button;
  }

  return (
    <Tooltip>
      <TooltipTrigger>{button}</TooltipTrigger>
      <TooltipContent>Preview not available</TooltipContent>
    </Tooltip>
  );
};

const getFormattedDescription = (voiceActor: VoiceActorDto) => {
  const description = voiceActor?.metadata?.description;
  const age = AgeMap[voiceActor?.metadata?.age || ""];

  if (description && age) {
    return `${description} - ${age}`;
  }

  return description || age || "";
};

const PreviewVoiceActor = ({
  previewVoiceActor,
  isPreviewVoiceActorPlaying,
  setIsPreviewVoiceActorPlaying,
  videoIsPlaying,
}: {
  previewVoiceActor: VoiceActorDto | null;
  isPreviewVoiceActorPlaying: boolean;
  setIsPreviewVoiceActorPlaying: (isPlaying: boolean) => void;
  videoIsPlaying: boolean;
}) => {
  const previewSrc = previewVoiceActor?.previewSrc;
  const [sampleDurationMs, setSampleDurationMs] = useState<number>(0);

  useEffect(() => {
    if (!previewSrc) {
      setSampleDurationMs(0);
      return;
    }

    const audio = new Audio(previewSrc);

    const handleLoadedMetadata = () => {
      setSampleDurationMs(audio.duration * 1000);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, [previewSrc]);

  if (!previewVoiceActor) {
    return null;
  }

  const description = getFormattedDescription(previewVoiceActor) || null;

  return (
    <div className="z-10 border-t border-grey-200 px-8 py-6">
      <div className="flex h-4 items-center gap-2">
        <h3 className="mx-1.5 truncate text-sm font-semibold text-text-primary">
          {previewVoiceActor?.name}
        </h3>
        {description && (
          <>
            <span className="text-text-secondary">-</span>
            <p className="truncate text-sm text-text-secondary capitalize">
              {description}
            </p>
          </>
        )}
      </div>
      {previewSrc && (
        <WaveformPlayer
          url={previewSrc}
          durationMs={sampleDurationMs}
          isPlaying={isPreviewVoiceActorPlaying}
          onPlay={() => setIsPreviewVoiceActorPlaying(true)}
          onPause={() => setIsPreviewVoiceActorPlaying(false)}
          videoIsPlaying={videoIsPlaying}
        />
      )}
    </div>
  );
};

export default VoiceActorDialog;
